import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	AUTH_ACTION_TYPE,
	AUTH_MESSAGE_CODES,
	AuthSelector,
	COUNTRY_OF_REGISTRATION,
	CustomerComplianceConfig,
	CustomerManagementAction,
	CustomerManagementSelector,
	MessageScopeType,
	MessageService,
	MessageType,
	SESSION_ACTION_TYPE,
} from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";
import { mapValues } from "lodash-es";
import { merge, of, timer } from "rxjs";
import { catchError, concatMap, delayWhen, filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../../state";
import { DataProtectionConfigService } from "../data-protection.config";

import { MARKETING_SETTINGS_ACTION_TYPE, MarketingSettingsAction } from "./marketing-settings.action";
import {
	MARKETING_CONSENT_FORM_NAME,
	MARKETING_SETTINGS_MESSAGE_CODE,
	MARKETING_SETTINGS_MESSAGE_CONTEXT,
} from "./marketing-settings.const";
import { Consents, MarketingSettings, MarketingSettingsWithTokenRequest } from "./marketing-settings.model";
import { MarketingSettingsService } from "./marketing-settings.service";

@Injectable()
export class MarketingSettingsEffect {
	updateCustomerSubscriptions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				// we need this for both regular and login flow success to have up to date data of user's subscriptions
				MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsSuccess,
				MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowSuccess
			),
			map((x: ActionWithPayload<MarketingSettings>) => x.payload),
			map((marketingSettings) => this.customerManagementAction.updateCustomerSubscriptions(marketingSettings))
		)
	);

	getCustomerOnUpdateConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsSuccess),
			map((x: ActionWithPayload<Partial<Consents>>) => x.payload),
			filter((consents) => consents.groupMarketing !== undefined || consents.brandMarketing !== undefined),
			map(() => this.customerManagementAction.getCustomer())
		)
	);

	getConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.getConsents, AUTH_ACTION_TYPE.sessionStart),
			filter(
				() =>
					!this.dataProtectionConfig.getCookieConsentDisabled() ||
					!this.dataProtectionConfig.getMarketingConsentDisabled()
			),
			switchMap(() =>
				this.service.getConsents().pipe(
					map((response) => this.action.getConsentsSuccess(response)),
					catchError((error) =>
						of(
							this.action.getConsentsFail(error),
							this.action.setDefaultConsents(this.dataProtectionConfig.getDefaultMarketingConsents())
						)
					)
				)
			)
		)
	);

	updateConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateConsents),
			map((x: ActionWithPayload<Partial<Consents>>) => x.payload),
			concatMap((action) => of(action).pipe(withLatestFrom(this.store.select(AuthSelector.isAuthenticated)))),
			filter(([, isAuthenticated]) => isAuthenticated),
			map(([consents]) => this.action.updateCustomerConsents(consents))
		)
	);

	updateCustomerConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsents),
			map((x: ActionWithPayload<Partial<Consents>>) => x.payload),
			switchMap((consents) =>
				this.service.updateConsents(consents).pipe(
					map(() => this.action.updateCustomerConsentsSuccess(consents)),
					catchError((error) => of(this.action.updateCustomerConsentsFail(error)))
				)
			)
		)
	);

	updateMarketingSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettings),
			map((x: ActionWithPayload<MarketingSettings>) => x.payload),
			switchMap((marketingSettings) =>
				this.service.updateMarketingSettings(marketingSettings).pipe(
					map(() => this.action.updateMarketingSettingsSuccess(marketingSettings)),
					catchError((error) => of(this.action.updateMarketingSettingsFail(error)))
				)
			)
		)
	);

	updateMarketingSettingsFromLoginFlow$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlow),
			map((x: ActionWithPayload<MarketingSettings>) => x.payload),
			switchMap((marketingSettings) =>
				this.service.updateMarketingSettings(marketingSettings).pipe(
					map(() => this.action.updateMarketingSettingsFromLoginFlowSuccess(marketingSettings)),
					catchError((error) => of(this.action.updateMarketingSettingsFromLoginFlowFail(error)))
				)
			)
		)
	);

	updateMarketingSettingsWithToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithToken),
			map((x: ActionWithPayload<MarketingSettingsWithTokenRequest>) => x.payload),
			switchMap((marketingSettings) =>
				this.service.updateMarketingSettingsWithToken(marketingSettings).pipe(
					withLatestFrom(
						this.store.select(CustomerManagementSelector.getPersistentData<string>(COUNTRY_OF_REGISTRATION))
					),
					delayWhen(([, countryCode]) => {
						if (countryCode) {
							const countryComplianceName =
								this.customerComplianceConfig.getComplianceByCountryCode(countryCode);

							return countryComplianceName
								? timer(this.customerComplianceConfig.getCountryKycPollingDelay(countryComplianceName)!)
								: of(0);
						}

						return of(0);
					}),
					map(() => this.action.updateMarketingSettingsWithTokenSuccess(marketingSettings)),
					catchError((error) => of(this.action.updateMarketingSettingsWithTokenFail(error)))
				)
			)
		)
	);

	loginFailed$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.loginFail,
				SESSION_ACTION_TYPE.loginWithPersonalNumberFail,
				SESSION_ACTION_TYPE.loginWithTokenFail,
				SESSION_ACTION_TYPE.loginWithVerificationCodeFail,
				SESSION_ACTION_TYPE.loginWithAuthenticationTokenFail
			),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((error) => error.code === AUTH_MESSAGE_CODES.login.consentsNotAccepted),
			map((response) =>
				this.action.consentsNotAccepted({
					subscriptions: response.data.consentsNotAcceptedLoginResponse.subscriptions,
					consents: mapValues(response.data.consentsNotAcceptedLoginResponse.consents, () => true),
					jurisdiction: response.data.consentsNotAcceptedLoginResponse.jurisidiction,
				})
			)
		)
	);

	clearConsentsOnSessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.clearConsents())
		)
	);

	clearMissingConsentsOnLogin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.login,
				SESSION_ACTION_TYPE.loginWithPersonalNumber,
				SESSION_ACTION_TYPE.loginWithVerificationCode
			),
			map(() => this.action.clearMissingConsents())
		)
	);

	clearDetails$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithTokenSuccess),
			map(() => this.action.clearMissingMarketingSettingsDetails())
		)
	);

	sendGTMEventAboutSuccessfulMarketingSettingUpdate$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowSuccess),
				tap(() => {
					this.telemetryService.track({
						event: GtmEventAction.GenericFormSuccess,
						FormName: MARKETING_CONSENT_FORM_NAME,
					});
				})
			),
		{ dispatch: false }
	);

	sendGTMEventAboutFailedMarketingSettingUpdate$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowFail),
				tap((action: ActionWithPayload<AppError>) => {
					this.telemetryService.track({
						event: GtmEventAction.GenericFormFailed,
						FormName: MARKETING_CONSENT_FORM_NAME,
						Error: action.payload.code,
					});
				})
			),
		{ dispatch: false }
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.map$({
				[MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsSuccess]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateCustomerConsentsSuccess,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsFail]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateCustomerConsentsFail,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsSuccess]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsSuccess,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFail]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsFail,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowSuccess]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsFromLoginFlowSuccess,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowFail]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsFromLoginFlowFail,
				},
				[MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithTokenFail]: {
					context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: MARKETING_SETTINGS_MESSAGE_CODE.updateMarketingSettingsWithTokenFail,
				},
			}),
			this.messageService.mapClearByContext$({
				[MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsents]: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: MarketingSettingsAction,
		private service: MarketingSettingsService,
		private messageService: MessageService,
		private dataProtectionConfig: DataProtectionConfigService,
		private store: Store<CommonState>,
		private customerComplianceConfig: CustomerComplianceConfig,
		private customerManagementAction: CustomerManagementAction,
		private telemetryService: TelemetryService
	) {}
}
